import { SerializedHighlight } from "@examspark/types";

export class Highlighter extends EventTarget {
  private container: HTMLElement;
  private restrictToElements: string[];

  static colors = {
    yellow: "#FDF6BE",
    red: "#FFC9C1",
    blue: "#C7E8F8",
    purple: "#DFE5FF",
  };

  constructor(
    container: HTMLElement,
    // css class names to restrict the highlighter to.
    // Items and their interactivity pose issues with our
    // serialization logic and until we have a better solution,
    // we restrict what students can highlight.
    restrictToElements: string[] = [],
  ) {
    super();
    this.container = container;
    this.restrictToElements = restrictToElements;
  }

  get highlightMarks() {
    return this.container.querySelectorAll("mark.exam-highlight");
  }

  serializeHighlights() {
    const serializedHighlights: SerializedHighlight[] = [];
    this.highlightMarks.forEach((highlight) => {
      const range = document.createRange();
      range.setStart(this.container, 0);
      range.setEndBefore(highlight);

      const highlightText = highlight.textContent;
      const beforeText = range.toString();

      if (!highlightText) {
        return;
      }

      serializedHighlights.push({
        text: highlightText,
        startOffset: beforeText.length,
        endOffset: beforeText.length + highlightText.length,
        color: (highlight as HTMLElement).dataset
          .color as keyof typeof Highlighter.colors,
      });
    });

    return serializedHighlights;
  }

  deserializeHighlights(serializedHighlights: SerializedHighlight[]): void {
    // remove all existing highlights
    const existingHighlights = this.container.querySelectorAll(
      "mark.exam-highlight",
    );
    existingHighlights.forEach((highlight) => {
      const parent = highlight.parentNode;
      if (parent) {
        highlight.replaceWith(...Array.from(highlight.childNodes));
        parent.normalize();
      }
    });

    // create new highlights
    serializedHighlights.forEach((sh) => {
      const treeWalker = document.createTreeWalker(
        this.container,
        NodeFilter.SHOW_TEXT,
      );

      // Iterate through all of the text nodes in the container
      // and find the start and end nodes for the highlight.
      // This is necessary because the highlight may span multiple text nodes
      // and we need to create a range that encompasses the entire highlight.
      let currentNode: Node | null = null;

      // we use this range to count characters for the start and end offsets
      const containerRange = document.createRange();
      containerRange.setStart(this.container, 0);

      const highlightRange = document.createRange();
      while ((currentNode = treeWalker.nextNode())) {
        const { startOffset, endOffset } = calculateNodeOffsets(
          this.container,
          currentNode,
        );

        if (sh.startOffset >= startOffset && sh.startOffset <= endOffset) {
          highlightRange.setStart(currentNode, sh.startOffset - startOffset);
        }
        if (sh.endOffset >= startOffset && sh.endOffset <= endOffset) {
          highlightRange.setEnd(currentNode, sh.endOffset - startOffset);
        }

        if (!highlightRange.collapsed) {
          if (sh.text !== highlightRange.toString()) {
            // console.warn(
            //   "Highlight text does not match. Did the item content change?",
            // );
            break;
          }
          this.createMark(highlightRange, sh.color ?? "yellow");
          break;
        }
      }
    });
  }

  shouldHighlight() {
    const selection = document.getSelection();
    if (!selection) {
      return false;
    }

    // check if the selection is within the container
    if (!this.container.contains(selection.anchorNode)) {
      return false;
    }

    if (selection.rangeCount === 0) {
      return false;
    }

    for (let i = 0; i < selection.rangeCount; i++) {
      const range = selection.getRangeAt(i);

      // do not allow highlighting if the selection crosses multiple elements
      if (range.startContainer !== range.endContainer) {
        // .. unlesss they are in the same parent (heuristic, could be improved)
        if (range.startContainer.parentNode !== range.endContainer.parentNode) {
          return false;
        }
      }

      // do not allow highlighting if the selection is empty
      if (range.collapsed) {
        return false;
      }

      // do not allow highlighting if the selection is within a
      // non-highlightable element
      const el = range.commonAncestorContainer;
      if (this.restrictToElements.length > 0) {
        const element =
          el.nodeType === Node.TEXT_NODE
            ? el.parentElement
            : (el as HTMLElement);
        if (
          element &&
          !this.restrictToElements.some(
            (restrictToElement) =>
              element.closest(restrictToElement) !== null ||
              element.matches(restrictToElement),
          )
        ) {
          return false;
        }
      }

      // only allow highlighting if the selection is within a text node
      // or is an existing highlight
      if (
        !(
          range.startContainer.nodeType === Node.TEXT_NODE ||
          range.startContainer.nodeName === "MARK"
        )
      ) {
        return false;
      }
    }

    return true;
  }

  static isExistingHighlight(el: Node | null): el is HTMLElement {
    if (!el || el.nodeType !== Node.ELEMENT_NODE) {
      return false;
    }

    const element = el as HTMLElement;
    return element.nodeName === "MARK";
  }

  removeHighlight() {
    const selection = document.getSelection();

    const el = selection?.anchorNode;
    if (!el || !Highlighter.isExistingHighlight(el)) {
      console.warn("Cannot remove highlight");
      return;
    }

    const parent = el.parentNode;
    if (parent) {
      el.replaceWith(...Array.from(el.childNodes));
      // this stitches any contiguous text nodes back together
      // so that we don't end up with a ton of fragments that
      // can't be highlighted again
      parent.normalize();

      this.dispatchEvent(new CustomEvent("highlight", {}));
    }

    selection.removeAllRanges();
  }

  createHighlight(color: keyof typeof Highlighter.colors = "yellow") {
    if (!this.shouldHighlight()) {
      console.warn("Cannot create highlight");
      return;
    }
    const selection = document.getSelection();
    if (!selection) {
      return;
    }

    const range = selection.getRangeAt(0);

    if (Highlighter.isExistingHighlight(range.startContainer)) {
      range.startContainer.style.backgroundColor = Highlighter.colors[color];
      selection?.removeAllRanges();
      return;
    }

    for (let i = 0; i < selection.rangeCount; i++) {
      const range = selection.getRangeAt(i).cloneRange();
      // In firefox, when selecting across a <select> element,
      // the selection's ranges split up. But the endContainer
      // is set to the <select> element and not the end of the text node,
      // we we need to move the end or else we will fail to surroundContents()
      if (range.endContainer.nodeType !== Node.TEXT_NODE) {
        range.setEndAfter(range.startContainer);
      }
      this.createMark(range, color);
    }

    selection!.removeAllRanges();
  }

  createMark(range: Range, color: keyof typeof Highlighter.colors = "yellow") {
    const highlight = document.createElement("mark");
    highlight.className = "exam-highlight";
    highlight.dataset.color = color;

    highlight.style.backgroundColor = Highlighter.colors[color];
    highlight.addEventListener("click", (e) => {
      const elRange = document.createRange();
      elRange.selectNodeContents(highlight);

      const selection = document.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(elRange);
    });

    // surroundContents can throw an error if it doesn't
    // like the range. still narrowing down the cause.
    try {
      range.surroundContents(highlight);
      this.dispatchEvent(new CustomEvent("highlight", {}));
    } catch (e) {
      console.warn("Highight failed", { range });
      console.error(e);
    }
  }
}

// utility function
const calculateNodeOffsets = (container: HTMLElement, node: Node) => {
  const containerRange = document.createRange();
  containerRange.setStart(container, 0);

  containerRange.setEndBefore(node);
  const startOffset = containerRange.toString().length;

  containerRange.setEndAfter(node);
  const endOffset = containerRange.toString().length;

  return { startOffset, endOffset };
};
